import React from "react";
import styled from "styled-components";
import { Shield, Eye, Database, Users, Lock, Globe, Mail } from "lucide-react";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.7;
  color: var(--text-color);
  background: var(--background-color);
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const LastUpdated = styled.p`
  color: var(--text-secondary-color);
  font-size: 0.9rem;
  margin-bottom: 2rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SectionContent = styled.div`
  color: var(--text-secondary-color);
  margin-bottom: 1.5rem;

  p {
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: var(--text-color);
    font-weight: 600;
  }
`;

const HighlightBox = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border-left: 4px solid var(--primary-color);
`;

const ContactInfo = styled.div`
  background: var(--surface-color);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const ContactTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ContactLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageContainer>
      <Header>
        <Title>
          <Shield size={32} />
          Privacy Policy
        </Title>
        <LastUpdated>Last updated: September 2025</LastUpdated>
      </Header>

      <Section>
        <SectionTitle>
          <Eye size={24} />
          Information We Collect
        </SectionTitle>
        <SectionContent>
          <p>
            We collect information you provide directly to us, such as when you
            create an account, create or share maps, or contact us for support.
          </p>

          <h4>Personal Information:</h4>
          <ul>
            <li>
              <strong>Account Information:</strong> Email address, display name,
              username, and profile picture
            </li>
            <li>
              <strong>Authentication Data:</strong> Information from Google
              Sign-In when you choose to use it
            </li>
            <li>
              <strong>Content:</strong> Maps, comments, and other content you
              create or share
            </li>
            <li>
              <strong>Communication:</strong> Messages you send to us through
              our contact system
            </li>
          </ul>

          <h4>Automatically Collected Information:</h4>
          <ul>
            <li>
              <strong>Usage Data:</strong> How you interact with our platform,
              features used, and time spent
            </li>
            <li>
              <strong>Device Information:</strong> Browser type, operating
              system, and device identifiers
            </li>
            <li>
              <strong>Location Data:</strong> General geographic location based
              on your IP address
            </li>
            <li>
              <strong>Cookies and Similar Technologies:</strong> Analytics
              cookies to enhance your experience
            </li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Database size={24} />
          How We Use Your Information
        </SectionTitle>
        <SectionContent>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our mapping platform</li>
            <li>Process transactions and send related information</li>
            <li>Send technical notices, updates, and support messages</li>
            <li>Respond to your comments and questions</li>
            <li>Monitor and analyze usage patterns and trends</li>
            <li>
              Detect, prevent, and address technical issues and security threats
            </li>
            <li>Comply with legal obligations and enforce our terms</li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Users size={24} />
          Information Sharing
        </SectionTitle>
        <SectionContent>
          <p>
            We do not sell, trade, or otherwise transfer your personal
            information to third parties, except:
          </p>
          <ul>
            <li>
              <strong>Public Content:</strong> Maps and comments you choose to
              make public are visible to other users
            </li>
            <li>
              <strong>Service Providers:</strong> Trusted third parties who
              assist in operating our platform (e.g., hosting, analytics)
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to
              protect our rights and safety
            </li>
          </ul>

          <HighlightBox>
            <strong>Important:</strong> Your personal information (email, real
            name) is never shared publicly. Only your chosen display name,
            username, and public maps are visible to other users.
          </HighlightBox>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Lock size={24} />
          Data Security
        </SectionTitle>
        <SectionContent>
          <p>
            We implement appropriate security measures to protect your personal
            information against unauthorized access, alteration, disclosure, or
            destruction. These include:
          </p>
          <ul>
            <li>Secure authentication and login systems</li>
            <li>Encrypted data transmission</li>
            <li>Input validation and spam protection</li>
            <li>Rate limiting to prevent abuse</li>
            <li>Secure cloud hosting</li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Globe size={24} />
          Cookies and Tracking
        </SectionTitle>
        <SectionContent>
          <p>We use the following cookies and similar technologies:</p>
          <ul>
            <li>
              <strong>Necessary cookies:</strong> Required for basic platform
              functionality (authentication, preferences)
            </li>
            <li>
              <strong>Analytics cookies:</strong> Help us understand how you use
              our platform
            </li>
          </ul>

          <p>
            You can control cookie preferences using our cookie banner, or
            through your browser settings. Disabling necessary cookies may
            affect platform functionality.
          </p>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Globe size={24} />
          Changes to This Policy
        </SectionTitle>
        <SectionContent>
          <p>
            We may update this privacy policy from time to time. We will notify
            you of any changes by posting the new policy on this page and
            updating the "Last updated" date. Your continued use of our platform
            after any changes constitutes acceptance of the updated policy.
          </p>
        </SectionContent>
      </Section>

      <ContactInfo>
        <ContactTitle>
          <Mail size={20} />
          Contact Us
        </ContactTitle>
        <p>
          If you have any questions about this privacy policy or our data
          practices, please{" "}
          <ContactLink href="/contact">contact us</ContactLink>.
        </p>
      </ContactInfo>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;
